import { FC, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Input from "shared/Input/Input";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Mail, Lock, ArrowRight, Shield, Users, Zap } from "lucide-react";

import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContext } from "context/appContext";
import { currentUser } from "redux/features/auth/authSlice";
import { initStates, login } from "redux/features/signIn/signInSlice";
import { resetValues } from "redux/features/signUp/signUpSlice";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

const HIW1img = `static-files/client_inscrit.png`;

export interface PageLoginProps {
  className?: string;
}

export interface accountTest {
  phone: number;
}

const formSchema = z.object({
  email: z
    .string({ required_error: "Veuillez entrer un email valide" })
    .email({ message: "Veuillez entrer un email valide" }),
  password: z
    .string()
    .regex(
      /((?=.*\d)(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).{8,26}$/,
      "Veuillez entrer un mot de passe valide."
    ),
});

const PageLogin: FC<PageLoginProps> = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  let redirect = "";
  if (location && location?.state?.form) {
    redirect = location?.state?.form?.pathname;
  }

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const signInState = useAppSelector((state) => state.signIn);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: "onSubmit",
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    setErrorAlert(false);
    dispatch(login(data));
  };

  useEffect(() => {
    load();

    if (authContext.isAuthenticated) {
      if (location.state && location.state.from) {
        navigate(redirect);
      }
      navigate("/");
    }
  }, []);

  useEffect(() => {
    switch (signInState.loading) {
      case "pending": {
        setIsLoading(true);
        break;
      }
      case "succeeded": {
        setIsLoading(false);
        // Petit délai pour s'assurer que le token est sauvegardé
        setTimeout(() => {
          dispatch(currentUser());
        }, 100);
        break;
      }
      case "failed": {
        setIsLoading(false);
        setErrorAlert(true);
        break;
      }
    }
  }, [signInState.loading]);

  useEffect(() => {
    if (authContext.user && authContext.isAuthenticated) {
      switch (authContext.user.role) {
        case "owner": {
          navigate("/owner");
          break;
        }
        case "client": {
          const targetPath = redirect || "/client/dashboard";
          navigate(targetPath);
          break;
        }
        case "admin": {
          const targetPath = redirect || "/dashboard";
          navigate(targetPath);
          break;
        }
        case "service": {
          const targetPath = redirect || "/";
          navigate(targetPath);
          break;
        }
        case "creator": {
          const targetPath = redirect || "/dashboard-creator";
          navigate(targetPath);
          break;
        }
        default: {
          navigate("/");
        }
      }
    }
  }, [authContext.user, authContext.isAuthenticated]);

  const load = async () => {
    await dispatch(initStates());
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    dispatch(resetValues());
  }, []);

  const features = [
    { icon: Shield, text: "Connexion sécurisée", color: "text-green-500" },
    { icon: Zap, text: "Accès instantané", color: "text-blue-500" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-blue-900/20 relative overflow-hidden">
      <Helmet>
        <title>Event To Bee | Connexion</title>
      </Helmet>

      {/* Background Effects */}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-purple-300/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              delay: Math.random() * 6,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen py-12 px-4">
        <div className="w-full max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Login Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="w-full max-w-md mx-auto lg:mx-0"
            >
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 lg:p-10 border border-white/20 dark:border-gray-700/20">
                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-center mb-8"
                >
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                    Se connecter
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">Accédez à votre espace Event To Bee</p>
                </motion.div>

                {/* Error Alert */}
                <AnimatePresence>
                  {errorAlert && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <div className="text-sm text-red-600">{signInState.signInError}</div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Form */}
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Adresse email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="votre@email.com"
                        className={`pl-12 h-12 border-2 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-purple-500/20 ${
                          errors.email?.message
                            ? "border-red-500"
                            : "border-gray-200 dark:border-gray-600 focus:border-purple-500"
                        }`}
                        disabled={isLoading}
                        {...register("email")}
                      />
                    </div>
                    {errors.email?.message && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-xs text-red-500 mt-2"
                      >
                        {errors.email?.message}
                      </motion.div>
                    )}
                  </div>

                  {/* Password Field */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Mot de passe
                      </label>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-purple-600 hover:text-purple-700 transition-colors"
                      >
                        Oublié ?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className={`pl-12 pr-12 h-12 border-2 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-purple-500/20 ${
                          errors.password?.message
                            ? "border-red-500"
                            : "border-gray-200 dark:border-gray-600 focus:border-purple-500"
                        }`}
                        disabled={isLoading}
                        {...register("password")}
                      />
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                      </motion.button>
                    </div>
                    {errors.password?.message && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-xs text-red-500 mt-2"
                      >
                        {errors.password?.message}
                      </motion.div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isLoading}
                    className="group w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Se connecter</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </motion.button>
                </motion.form>

                {/* Features */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700"
                >
                  <div className="grid grid-cols-1 gap-3">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <feature.icon className={`w-5 h-5 ${feature.color}`} />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Side - Sign Up Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="w-full max-w-md mx-auto lg:mx-0"
            >
              <div className="relative">
                <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.3 }}>
                  <Link
                    to="/signup/client"
                    className="group block bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20 dark:border-gray-700/20 hover:shadow-3xl transition-all duration-300"
                  >
                    <div className="text-center">
                      <div className="relative mb-6">
                        <img
                          src={process.env.REACT_APP_CLOUD_FRONT + HIW1img}
                          alt="Compte Utilisateur"
                          className="w-24 h-24 mx-auto rounded-2xl group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>

                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Nouveau sur Event To Bee ?
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Créez votre compte utilisateur pour accéder à tous nos espaces événementiels
                      </p>

                      <div className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold group-hover:from-green-600 group-hover:to-emerald-600 transition-all duration-300">
                        <span>Créer un compte</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>

                {/* Decorative Elements */}
                <motion.div
                  className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full opacity-20 blur-xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.2, 0.3, 0.2],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLogin;
